import React from 'react';
import { Stack, TextField, Typography, FormControl, Autocomplete } from '@mui/material';
import { TicketInfoSectionProps } from '../../../@types/transactions';

const TicketInfoSection: React.FC<TicketInfoSectionProps> = ({
  values,
  touched,
  errors,
  getFieldProps,
  setFieldValue,
  isEdit,
  bridgeIdentity,
  drivers,
  vehicleReg,
  haulier,
  client
}) => (
  <>
    <Typography variant="h6" gutterBottom>
      Ticket Info
    </Typography>

    <Stack direction="row" spacing={2}>
      {isEdit && (
        <TextField
          fullWidth
          label="Ticket Number"
          disabled
          {...getFieldProps('TicketNumber')}
          error={Boolean(touched.TicketNumber && errors.TicketNumber)}
          helperText={touched.TicketNumber && errors.TicketNumber}
        />
      )}

      <FormControl fullWidth>
        <Autocomplete
          options={bridgeIdentity}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.Id === value.Id}
          value={
            bridgeIdentity?.find(
              (option: { Id: number; name: string }) => option.name === values.BridgeIdentity
            ) || null
          }
          onChange={(event, newValue) => {
            setFieldValue && setFieldValue('BridgeIdentity', newValue ? newValue.name : '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Bridge Identity*"
              error={Boolean(touched.BridgeIdentity && errors.BridgeIdentity)}
              helperText={touched.BridgeIdentity && errors.BridgeIdentity}
            />
          )}
        />
      </FormControl>

      <FormControl fullWidth>
        <Autocomplete
          options={drivers}
          getOptionLabel={(option: { value: string; id: number }) => option.value || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            drivers?.find(
              (option: { value: string; id: number }) => option.id === values.DriverId
            ) || null
          }
          onChange={(event, newValue) => {
            setFieldValue && setFieldValue('DriverName', newValue ? newValue.value : '');
            setFieldValue && setFieldValue('DriverId', newValue ? newValue.id : null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Driver*"
              error={Boolean(touched.DriverName && errors.DriverName)}
              helperText={touched.DriverName && errors.DriverName}
            />
          )}
        />
      </FormControl>
    </Stack>

    <Stack direction="row" spacing={2}>
      <FormControl fullWidth>
        <Autocomplete
          options={vehicleReg}
          getOptionLabel={(option: any) => option.registrationPlate || ''}
          isOptionEqualToValue={(option, value) =>
            option.registrationPlate === value.registrationPlate
          }
          value={
            vehicleReg?.find((type: any) => type.registrationPlate === values.VehicleReg) || null
          }
          onChange={(_, value) =>
            setFieldValue && setFieldValue('VehicleReg', value?.registrationPlate || '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vehicle Reg*"
              error={Boolean(touched.VehicleReg && errors.VehicleReg)}
              helperText={touched.VehicleReg && errors.VehicleReg}
            />
          )}
          renderOption={(props, option: any) => (
            <li {...props} key={option.id}>
              <div>
                <strong>{option.registrationPlate}</strong>
                <br />
                <small style={{ color: 'gray' }}>{option.description}</small>
              </div>
            </li>
          )}
        />
      </FormControl>

      <FormControl fullWidth>
        <Autocomplete
          options={haulier}
          getOptionLabel={(option: any) => option.value || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={isEdit}
          value={
            haulier?.find((type: any) => type.id === values.HaulierId) ||
            haulier?.find(
              (type: any) =>
                type.value?.toLowerCase().trim() === values.Haulier?.toLowerCase().trim()
            ) ||
            null
          }
          onChange={(event, value) => {
            setFieldValue && setFieldValue('Haulier', value?.value || '');
            setFieldValue && setFieldValue('HaulierId', value?.id || null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Haulier*"
              error={Boolean(touched.Haulier && errors.Haulier)}
              helperText={touched.Haulier && errors.Haulier}
            />
          )}
          renderOption={(props, option: any) => (
            <li {...props} key={option.id}>
              <div>
                <strong>{option.value}</strong>
                <br />
                <small style={{ color: 'gray' }}>{option.accountRef}</small>
              </div>
            </li>
          )}
        />
      </FormControl>

      <FormControl fullWidth>
        <Autocomplete
          options={client}
          getOptionLabel={(option: any) => option.value || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={isEdit}
          value={client?.find((type: any) => type.id === values.ClientId) || null}
          onChange={(event, value) => {
            setFieldValue && setFieldValue('Client', value?.value || '');
            setFieldValue && setFieldValue('ClientId', value?.id || null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client*"
              error={Boolean(touched.Client && errors.Client)}
              helperText={touched.Client && errors.Client}
            />
          )}
          renderOption={(props, option: any) => (
            <li {...props} key={option.id}>
              <div>
                <strong>{option.value}</strong>
                <br />
                <small style={{ color: 'gray' }}>{option.accountRef}</small>
              </div>
            </li>
          )}
        />
      </FormControl>
    </Stack>

    <Stack direction="row" spacing={2}>
      <TextField
        fullWidth
        label="Quote No"
        disabled={isEdit}
        {...getFieldProps('QuoteNo')}
        error={Boolean(touched.QuoteNo && errors.QuoteNo)}
        helperText={touched.QuoteNo && errors.QuoteNo}
      />
      <TextField
        fullWidth
        label="Service Id"
        type="number"
        disabled={isEdit}
        {...getFieldProps('ServiceId')}
        error={Boolean(touched.ServiceId && errors.ServiceId)}
        helperText={touched.ServiceId && errors.ServiceId}
        InputLabelProps={isEdit && values.ServiceId ? { shrink: true } : undefined}
      />
    </Stack>
  </>
);

export default TicketInfoSection;
